<script setup lang="ts">
/**
 * `/reset-password?token=…`: the page a reset email opens. Setting a password
 * revokes every session server-side, so success ends at "sign in".
 *
 * The same steel card as the join form (AuthShell): two password wells, the
 * captcha well, the yellow plate to submit, a refusal line if the server says
 * no, and a quiet plate to back out.
 */
import { computed, ref } from 'vue';

import { useI18n } from '@/i18n';
import AuthShell from './AuthShell.vue';
import AuthNotice from './auth/AuthNotice.vue';
import AuthStatus from './auth/AuthStatus.vue';
import CaptchaWell from './auth/CaptchaWell.vue';
import GameIcon from './kit/GameIcon.vue';
import ActionButton from './panel/ActionButton.vue';
import PasswordField from './PasswordField.vue';
import { ApiError, api } from '@/net/api';

const emit = defineEmits<{ done: [] }>();
const props = defineProps<{ token: string }>();

const { t } = useI18n();

const PASSWORD_MIN = 8;

const password = ref('');
const confirm = ref('');
const altchaPayload = ref('');
const captcha = ref<InstanceType<typeof CaptchaWell> | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const done = ref(false);

const mismatch = computed(() => confirm.value.length > 0 && password.value !== confirm.value);

const canSubmit = computed(
  () =>
    !busy.value &&
    altchaPayload.value !== '' &&
    password.value.length >= PASSWORD_MIN &&
    password.value === confirm.value,
);

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  busy.value = true;
  error.value = null;
  try {
    await api.resetPassword(props.token, password.value, altchaPayload.value);
    done.value = true;
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : t('auth.unreachable');
    captcha.value?.rerun();
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <AuthShell :locked="busy" test-id="reset-screen" icon="lock" :title="t('reset.title')">
    <template v-if="done">
      <AuthStatus icon="medal" tone="good" :title="t('reset.ok')" data-test="reset-ok">{{ t('reset.okLine') }}</AuthStatus>
      <ActionButton variant="primary" block size="lg" data-test="reset-continue" @click="emit('done')">
        <GameIcon name="anchor" :size="40" />
        {{ t('reset.signIn') }}
      </ActionButton>
    </template>

    <form v-else class="reset-form" @submit.prevent="submit">
      <PasswordField
        id="new-password"
        v-model="password"
        label-hidden
        autocomplete="new-password"
        data-test="reset-password"
        :label="t('reset.newPassword')"
        :placeholder="t('reset.newPlaceholder', { count: PASSWORD_MIN })"
        :disabled="busy"
      />

      <PasswordField
        id="confirm-password"
        v-model="confirm"
        label-hidden
        autocomplete="new-password"
        data-test="reset-confirm"
        :label="t('reset.repeat')"
        :placeholder="t('reset.repeat')"
        :disabled="busy"
      />
      <AuthNotice v-if="mismatch" data-test="reset-mismatch">{{ t('reset.mismatch') }}</AuthNotice>

      <CaptchaWell ref="captcha" v-model="altchaPayload" test-id="altcha" />

      <AuthNotice v-if="error" tone="error" data-test="reset-error">{{ error }}</AuthNotice>

      <ActionButton type="submit" variant="primary" block size="lg" data-test="reset-submit" :disabled="!canSubmit" :loading="busy">
        <GameIcon name="lock" :size="40" />
        {{ busy ? t('auth.working') : t('reset.submit') }}
      </ActionButton>
    </form>

    <template v-if="!done" #after>
      <ActionButton size="sm" data-test="reset-cancel" @click="emit('done')">{{ t('reset.cancel') }}</ActionButton>
    </template>
  </AuthShell>
</template>

<style scoped>
.reset-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
